import {
  SOW_LABEL_BY_VALUE,
  buildDefaultSupportingDocumentPolicy,
  buildSowEntryFromPolicy,
  buildSowRequirementDescription,
  buildSowRequirementOptions,
  normalizeRelationshipRiskLevel,
  normalizeSowKey as normalizePolicySowKey,
  riskPolicy as supportingDocRiskPolicy,
  sanitizeSupportingDocumentPolicy,
} from "./sowDocumentPolicy";

export const EMBEDDED_DOC_MAX_FILE_BYTES = 25 * 1024 * 1024;

export const EMBEDDED_SOW_DOC_RULES = {
  employment: [
    { key: "payslips", label: "Payslips", isBankStatement: false },
    { key: "employment_contract", label: "Employment contract", isBankStatement: false },
    { key: "tax_return", label: "Tax return", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  self_employed: [
    { key: "contracts", label: "Copies of contracts", isBankStatement: false },
    { key: "tax_return", label: "Tax return", isBankStatement: false },
    { key: "audited_fs", label: "Audited financial statement(s)", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  business_owner: [
    { key: "shareholder_register", label: "Shareholder register", isBankStatement: false },
    { key: "audited_fs", label: "Audited financial statement(s)", isBankStatement: false },
    { key: "corporate_doc", label: "Corporate document", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  sale_business: [
    { key: "sale_agreement", label: "Sale agreement / SPA", isBankStatement: false },
    { key: "completion_statement", label: "Completion statement", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  sale_real_estate: [
    { key: "sale_agreement", label: "Sale agreement", isBankStatement: false },
    { key: "title_deed", label: "Title deed", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  rental_income: [
    { key: "rental_contract", label: "Rental contract", isBankStatement: false },
    { key: "title_deed", label: "Title deed", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  investment_returns: [
    { key: "broker_statement", label: "Broker / portfolio statement", isBankStatement: false },
    { key: "trade_confirmation", label: "Trade confirmation(s)", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  inheritance: [
    { key: "inheritance_certificate", label: "Certificate of inheritance", isBankStatement: false },
    { key: "grant_of_probate", label: "Grant of probate", isBankStatement: false },
    { key: "will_copy", label: "Copy of will", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  gift: [
    { key: "gift_deed", label: "Gift deed", isBankStatement: false },
    { key: "donor_explanation", label: "Donor letter / explanation", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  legal_settlement: [
    { key: "court_order", label: "Court order / settlement agreement", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  divorce_settlement: [
    { key: "divorce_decree", label: "Divorce decree / court order", isBankStatement: false },
    { key: "financial_settlement", label: "Financial settlement agreement", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  virtual_asset_gains: [
    { key: "exchange_statement", label: "Exchange statement", isBankStatement: false },
    { key: "trade_history", label: "Trade history / transaction report", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  loan: [
    { key: "loan_agreement", label: "Loan agreement", isBankStatement: false },
    { key: "repayment_schedule", label: "Repayment schedule", isBankStatement: false },
    { key: "bank_statements", label: "Bank statement(s)", isBankStatement: true },
  ],
  other: [],
};

export const EMBEDDED_SOW_LABELS = {
  employment: "Employment",
  self_employed: "Self-employed",
  business_owner: "Business owner",
  sale_business: "Sale of company",
  sale_real_estate: "Real estate sale",
  rental_income: "Rental income",
  investment_returns: "Investment returns",
  inheritance: "Inheritance",
  gift: "Gift",
  legal_settlement: "Legal settlement",
  divorce_settlement: "Divorce settlement",
  virtual_asset_gains: "Virtual asset gains",
  loan: "Loan",
  other: "Other",
};

export function embeddedSafeStr(value) {
  return String(value || "").trim();
}

export function sanitizeEmbeddedFilename(name) {
  const n = embeddedSafeStr(name);
  if (!n) return "file";
  return n
    .replace(/[\/\\]/g, "_")
    .replace(/[^\w.\-() ]+/g, "_")
    .slice(0, 160);
}

export function embeddedMakeId() {
  return (
    (globalThis.crypto?.randomUUID?.() ||
      `id_${Date.now()}_${Math.random().toString(16).slice(2)}`) + ""
  );
}

export function formatEmbeddedBytes(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let val = n;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function slugId(value) {
  return (
    embeddedSafeStr(value)
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "x"
  );
}

function canonicalCountry(value) {
  return embeddedSafeStr(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 12);
}

function embeddedCustomerPartyId({ name, nationality, domicile }) {
  return [
    "person",
    slugId(name) || "unknown",
    canonicalCountry(nationality) || "xx",
    canonicalCountry(domicile) || "xx",
  ].join(":");
}

function normalizeSowKey(value) {
  return normalizePolicySowKey(value);
}

// Entity client: its registration documents for the country of incorporation, as returned by
// GET /myKyc/clients-side/kyc/corporate-documents ({ cases: [{ name, logic, docs: [{ label, secondaryLabel, optional }] }] }).
// Same card id as the server (myKyc_client_kyc_submission.js buildEmbeddedEntityDocumentRequirements).
export function corporateDocumentsDescription(corporateDocuments) {
  const cases = Array.isArray(corporateDocuments?.cases) ? corporateDocuments.cases : [];
  if (!cases.length) return "• Company register extract or equivalent corporate registration document";
  const display = (d) => `• ${d.label}${d.secondaryLabel ? ` (${d.secondaryLabel})` : ""}${d.optional ? " (optional)" : ""}`;
  const several = cases.length > 1;
  const lines = [several ? "Provide one of the following:" : cases[0].logic === "ONE_OF" ? "Provide at least one of:" : "Required:"];
  cases.forEach((kase) => {
    if (several) lines.push(`${kase.name || "Option"} (${kase.logic === "ONE_OF" ? "at least 1 of" : "all required"}):`);
    (kase.docs || []).forEach((d) => lines.push(display(d)));
  });
  return lines.join("\n");
}

function buildEmbeddedEntityRequirements(form, fallbackName, corporateDocuments) {
  const customer = form?.customer || {};
  const name = embeddedSafeStr(customer.name || fallbackName || "Client");
  const country = embeddedSafeStr(customer.nationalityOrIncorporationCountry).toUpperCase();
  const partyId = ["entity", slugId(name) || "unknown", canonicalCountry(country) || "xx"].join(":");
  if (form?.documentsChecklist?.parties?.[partyId]?.docs?.reg?.obtained === true) return [];
  return [{
    id: "1",
    cardId: `party_${slugId(partyId)}_reg`,
    // Same wording as the API (myKyc_client_kyc_submission.js corporateDocumentsSection).
    section: (corporateDocuments?.docTypes || []).some((type) => type === "constitutional" || type === "ownership")
      ? "Company documents" : "Company registration documents",
    title: `${name}${country ? ` (Country of incorporation: ${country})` : ""}`,
    description: corporateDocumentsDescription(corporateDocuments),
    meta: {
      kind: "entity",
      partyId,
      partyName: name,
      partyRole: "Client",
      incorporationIso2: country,
      domicileIso2: embeddedSafeStr(customer.country).toUpperCase(),
    },
    options: [{ key: `party:${partyId}:reg`, label: "Company registration document" }],
  }];
}

export function buildEmbeddedKycDocumentRequirements(
  form,
  fallbackName = "Client",
  supportingDocumentPolicy,
  corporateDocuments = null
) {
  if (embeddedSafeStr(form?.customer?.type).toLowerCase() === "entity") {
    return buildEmbeddedEntityRequirements(form, fallbackName, corporateDocuments);
  }
  const customer = form?.customer || {};
  const name = embeddedSafeStr(customer.name || fallbackName || "Client");
  const nationality = embeddedSafeStr(customer.nationalityOrIncorporationCountry).toUpperCase();
  const domicile = embeddedSafeStr(customer.country).toUpperCase();
  const partyId = embeddedCustomerPartyId({ name, nationality, domicile });
  const partyCardId = `party_${slugId(partyId)}`;
  const checklistDocs =
    form?.documentsChecklist?.parties?.[partyId]?.docs &&
    typeof form.documentsChecklist.parties[partyId].docs === "object"
      ? form.documentsChecklist.parties[partyId].docs
      : {};
  const idObtained = checklistDocs?.id?.obtained === true;
  const poaObtained = checklistDocs?.poa?.obtained === true;

  const requirements = [];

  if (!idObtained) {
    requirements.push({
      id: "1",
      cardId: `${partyCardId}_id`,
      section: "Identification documents",
      title: `${name}${nationality ? ` (Nationality: ${nationality})` : ""}`,
      description:
        "• Passport or national ID\n(If the document has two sides, upload both front and back)",
      meta: {
        kind: "individual",
        partyId,
        partyName: name,
        partyRole: "Client",
        nationalityIso2: nationality,
        domicileIso2: domicile,
      },
      options: [
        {
          key: `party:${partyId}:id`,
          label: "Passport or national ID",
        },
      ],
    });
  }

  if (!poaObtained) {
    requirements.push({
      id: "2",
      cardId: `${partyCardId}_poa`,
      section: "Identification documents",
      title: `${name}${domicile ? ` (Domicile: ${domicile})` : ""}`,
      description: "• Proof of address (e.g. utility bill)",
      meta: {
        kind: "individual",
        partyId,
        partyName: name,
        partyRole: "Client",
        nationalityIso2: nationality,
        domicileIso2: domicile,
      },
      options: [{ key: `party:${partyId}:poa`, label: "Proof of address (e.g. utility bill)" }],
    });
  }

  return requirements;
}

export function buildEmbeddedCardSnapshots(requirements) {
  const out = {};
  (requirements || []).forEach((r) => {
    const cardId = embeddedSafeStr(r.cardId);
    if (!cardId) return;
    out[cardId] = {
      id: cardId,
      section: embeddedSafeStr(r.section) || null,
      title: embeddedSafeStr(r.title) || null,
      description: embeddedSafeStr(r.description) || null,
      meta: r.meta || null,
      options: Array.isArray(r.options) ? r.options : [],
    };
  });
  return out;
}
